import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import './alumni.css';
import './AlumniAcademicTheme.css';
import { ProviderButton } from './AlumniIcons';
const ROOT = `${API_URL}/api/alumni/auth`;
async function post(path, body) {
  const response = await fetch(`${ROOT}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw Error(result.error || `Request failed (${response.status})`);
  return result;
}
export default function AlumniLogin() {
  const navigate = useNavigate();
  const [whatsapp, setWhatsapp] = useState('');
  const [registrationLink, setRegistrationLink] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState('start');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true); setError(''); try {
      await fn();
    }
      catch (e) {
        setError(e.message || 'Sign-in failed');
      }
      finally {
        setBusy(false);
      }
  };
  const signIn = token => {
    if (!token)
      throw Error('No alumni session returned'); localStorage.setItem('alumniToken', token); navigate('/alumni/dashboard', { replace: true });
  };
  const oauth = provider => run(async () => { const { url } = await post('/oauth/start', { provider, mode: 'login' }); window.location.assign(url); });
  const passkey = () => run(async () => { const { startAuthentication } = await import('@simplewebauthn/browser'); const options = await post('/passkeys/login/options', {}); const response = await startAuthentication({ optionsJSON: options }); const result = await post('/passkeys/login/verify', { challenge: options.challenge, response }); signIn(result.alumniToken); });
  const startOtp = () => run(async () => { await post('/otp/start', { whatsapp }); setStage('verify'); });
  const verifyOtp = () => run(async () => { const result = await post('/otp/verify', { whatsapp, code }); signIn(result.alumniToken); });
  return <main className="al-bg al-academic-theme">
    <div className="al-card al-card-login">
      <header className="al-header">
        <div className="al-kicker">
          UNIFLOO · ALUMNI CONNECT
        </div>
        <h1>
          Welcome back
        </h1>
        <p>
          Sign in to your alumni profile
        </p>
      </header>
      <section className="al-body">
        {error && <p className="al-error" role="alert">
          {error}
        </p>}
        <div className="al-section-tag">
          SECURE ACCESS
        </div>
        <h2>
          Choose how to sign in
        </h2>
        <p className="al-muted">
          Use a method you previously connected to your alumni account.
        </p>
        <AlumniSignInMethods />
        <div className="al-divider">
          or sign in with WhatsApp OTP
        </div>
        <p>
          Already registered? Enter your WhatsApp number to receive a one-time sign-in code. No registration link is needed.
        </p>
        <label className="al-field">
          WhatsApp number (international format)
          <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+919876543210" autoComplete="tel" />
        </label>
        {stage === 'start' ? <button type="button" className="al-primary" disabled={busy || !/^\+[1-9]\d{7,14}$/.test(whatsapp)} onClick={startOtp}>
          Send WhatsApp OTP
        </button> : <>
          <label className="al-field">
            6-digit verification code
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
          </label>
          <button className="al-primary" disabled={busy || code.length !== 6} onClick={verifyOtp}>
            Verify and sign in
          </button>
          <button className="al-outline" disabled={busy} onClick={() => { setStage('start'); setCode(''); }}>
            Request another code
          </button>
        </>}
        <div className="al-divider">
          New to Alumni Connect?
        </div>
        <p>
          Enter your campus invitation link to register securely.
        </p>
        <label className="al-field">
          Campus registration link or code
          <input value={registrationLink} onChange={e => setRegistrationLink(e.target.value)} placeholder="Paste your invitation link" />
        </label>
        <button type="button" className="al-outline" onClick={() => {
          const raw = registrationLink.trim(); const slug = raw.includes("/alumni/register/") ? raw.split("/alumni/register/")[1].split(/[?#/]/)[0] : raw; if (!/^[a-f0-9]{16,100}$/i.test(slug)) {
            setError("Enter a valid campus invitation link or code.");
            return;
          } navigate(`/alumni/register/${encodeURIComponent(slug)}`);
        }}>
          Create alumni account
        </button>
      </section>
    </div>
  </main>;
}
export function AlumniSignInMethods() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true); setError(''); try {
      await fn();
    }
      catch (e) {
        setError(e?.message || 'Authentication failed');
      }
      finally {
        setBusy(false);
      }
  };
  const oauth = provider => run(async () => { const { url } = await post('/oauth/start', { provider, mode: 'login' }); window.location.assign(url); });
  const passkey = () => run(async () => {
    if (!window.PublicKeyCredential || !window.isSecureContext)
      throw Error('Passkeys require HTTPS or localhost and a compatible browser.'); const { startAuthentication } = await import('@simplewebauthn/browser'); const options = await post('/passkeys/login/options', {}); const response = await startAuthentication({ optionsJSON: options }); const result = await post('/passkeys/login/verify', { challenge: options.challenge, response }); if (!result.alumniToken)
      throw Error('Session missing'); localStorage.setItem('alumniToken', result.alumniToken); navigate('/alumni/dashboard', { replace: true });
  });
  return <>
    <div className="al-auth al-provider-grid">
      <ProviderButton provider="passkey" disabled={busy} onClick={passkey}>
        Continue with passkey
      </ProviderButton>
      {['google', 'apple', 'linkedin'].map(provider => <ProviderButton provider={provider} key={provider} disabled={busy} onClick={() => oauth(provider)}>
        Continue with
        {provider[0].toUpperCase() + provider.slice(1)}
      </ProviderButton>)}
    </div>
    {error && <p className="al-error" role="alert">
      {error}
    </p>}
  </>;
}
export function AlumniAuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1)); if (params.get('error')) {
      setError(params.get('error'));
      return;
    } const code = params.get('code'); if (!code) {
      setError('Missing login authorization code');
      return;
    } post('/exchange', { code }).then(data => {
      if (!data.alumniToken)
        throw Error('Session missing'); localStorage.setItem('alumniToken', data.alumniToken); navigate('/alumni/dashboard', { replace: true });
    }).catch(e => setError(e.message));
  }, [navigate]);
  return <main className="al-bg al-academic-theme">
    <div className="al-card al-card-login">
      <section className="al-body">
        {error || 'Completing secure sign-in…'}
      </section>
    </div>
  </main>;
}
